import AlertBanner from '@/components/common/AlertBanner';
import GlassCard from '@/components/GlassCard';
import { router } from '@inertiajs/react';
import React from 'react';

interface CalendarEmbedCardProps {
    calendarSrc: string | null;
    calendarTokenExpiresAt: string | null;
}

// Refresh once the token has this little life left, so a booking in progress does not
// die halfway through.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

// The calendar app renders its own page padding above the booking card, which inside an
// iframe reads as a large dead gap under our copy. It is cross-origin, so we cannot
// restyle it -- the frame is pulled up by that much instead and the card clips the
// overflow. Purely cosmetic: if the calendar team ever ships an embed mode without the
// page chrome, drop this and the matching height compensation below.
const EMBED_TOP_TRIM_PX = 88;

const EMBED_MIN_HEIGHT_PX = 760;

const CalendarEmbedCard: React.FC<CalendarEmbedCardProps> = ({
    calendarSrc,
    calendarTokenExpiresAt,
}) => {
    // Back/Forward makes Inertia restore these props from history with no server
    // request, so a token minted an hour ago can be remounted into the iframe. One
    // refresh per mount is enough, and the guard also stops a skewed client clock
    // from looping.
    const hasRefreshed = React.useRef(false);

    React.useEffect(() => {
        if (!calendarTokenExpiresAt || hasRefreshed.current) {
            return;
        }

        const checkFreshness = () => {
            const remaining =
                new Date(calendarTokenExpiresAt).getTime() - Date.now();

            if (remaining > REFRESH_MARGIN_MS || hasRefreshed.current) {
                return;
            }

            hasRefreshed.current = true;
            router.reload({
                only: ['calendarSrc', 'calendarTokenExpiresAt'],
            });
        };

        checkFreshness();

        // A tab left open past the hour is the same problem without the history step.
        document.addEventListener('visibilitychange', checkFreshness);

        return () => {
            document.removeEventListener('visibilitychange', checkFreshness);
        };
    }, [calendarTokenExpiresAt]);

    if (!calendarSrc) {
        return (
            <AlertBanner variant="amber">
                Ahora mismo no puedes reservar clases 1-on-1. Esto suele
                deberse a que tu matrícula está pausada o no incluye estas
                sesiones. Si crees que se trata de un error, escríbenos y lo
                revisamos.
            </AlertBanner>
        );
    }

    return (
        <GlassCard className="overflow-hidden p-0">
            <iframe
                src={calendarSrc}
                title="Reservar clase"
                style={{
                    width: '100%',
                    // Height grows by what the top trim hides, so nothing is lost off
                    // the bottom of the booking form.
                    minHeight: EMBED_MIN_HEIGHT_PX + EMBED_TOP_TRIM_PX,
                    marginTop: -EMBED_TOP_TRIM_PX,
                    border: 0,
                    display: 'block',
                }}
            />
        </GlassCard>
    );
};

export default CalendarEmbedCard;
